/**
 * AdminSideNav — the §14.4 destination rail on ui-web's SideNav primitives
 * (M6-01). Brand is server-driven (§14.1.1: logo mark + app name from the
 * theme service). RBAC-aware: destinations whose contract `view` permission
 * the actor lacks are HIDDEN, and a group with nothing visible disappears
 * with its heading (§11.1 — the server stays the authority).
 */

import type { MouseEvent, ReactNode } from "react";
import { useHref, useLinkClickHandler, useLocation } from "react-router-dom";
import { SideNav, SideNavItem, SideNavSection, useTheme } from "@platform/ui-web";
import { listMerchantApplications } from "../api/merchantsApi";
import { useAuth } from "../auth/AuthProvider";
import { useApiClient } from "../data/useApiClient";
import { useFeatureFlags } from "../data/useFeatureFlag";
import { useQuery } from "../data/useQuery";
import { useI18n } from "../i18n/I18nProvider";
import { NAV_GROUPS, type NavItem } from "./nav";

function RouterNavItem({ item, badge }: { readonly item: NavItem; readonly badge?: ReactNode }) {
  const { t } = useI18n();
  const location = useLocation();
  const to = item.path === "" ? "/" : `/${item.path}`;
  const href = useHref(to);
  const handleClick = useLinkClickHandler(to);
  const active =
    item.path === "" ? location.pathname === "/" : location.pathname === to || location.pathname.startsWith(`${to}/`);
  const Icon = item.icon;
  return (
    <SideNavItem
      icon={<Icon size={19} strokeWidth={1.8} />}
      label={t(item.labelKey)}
      active={active}
      href={href}
      {...(badge !== undefined && badge !== null ? { badge } : {})}
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        handleClick(event);
      }}
    />
  );
}

/**
 * PENDING application count for the sidebar badge (M7-43b) — one cheap
 * `per_page=1` page whose `meta.total` IS the count; fetched only when the
 * destination is visible at all, and silently absent on failure (a nav badge
 * is decoration, never the queue's source of truth).
 */
function usePendingApplicationsCount(enabled: boolean): number {
  const client = useApiClient();
  const { data } = useQuery(
    () =>
      enabled
        ? listMerchantApplications(client, { status: "PENDING", page: 1, per_page: 1 })
        : Promise.resolve(null),
    `nav-pending-applications:${String(enabled)}`,
  );
  return data?.meta.total ?? 0;
}

export function AdminSideNav() {
  const { t, locale, formatNumber } = useI18n();
  const { theme } = useTheme();
  const { hasPermission } = useAuth();
  const flags = useFeatureFlags();
  const appName = theme?.brand.app_name[locale] ?? t("app.name");
  const pendingApplications = usePendingApplicationsCount(hasPermission("merchants.application.view"));

  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) => (item.permission === null || hasPermission(item.permission)) && (item.flag === undefined || flags.enabled(item.flag)),
    ),
  })).filter((group) => group.items.length > 0);

  return (
    <SideNav
      label={appName}
      brand={
        <span className="shell-brand" title={appName}>
          {theme !== null ? (
            <img className="shell-brand-logo" src={theme.brand.logo.mark} alt="" aria-hidden="true" />
          ) : (
            <span className="shell-brand-mark" aria-hidden="true">
              {appName.charAt(0)}
            </span>
          )}
          <span className="shell-brand-name">{appName}</span>
        </span>
      }
    >
      {visibleGroups.map((group) => (
        <SideNavSection key={group.labelKey} title={t(group.labelKey)}>
          {group.items.map((item) => (
            <RouterNavItem
              key={item.labelKey}
              item={item}
              {...(item.path === "merchant-applications" && pendingApplications > 0
                ? { badge: formatNumber(pendingApplications) }
                : {})}
            />
          ))}
        </SideNavSection>
      ))}
    </SideNav>
  );
}
